import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, timestampedSchema, uuidSchema } from '../schemas/common';

export const costumeSizeKindSchema = z.enum(['child', 'adult', 'shoe', 'custom']);
export type CostumeSizeKind = z.infer<typeof costumeSizeKindSchema>;

export const packageItemStatusSchema = z.enum([
  'not_started',
  'ordered',
  'received',
  'delivered',
  'waived',
]);
export type PackageItemStatus = z.infer<typeof packageItemStatusSchema>;

export const costumeSizeInputSchema = z.object({
  dancer_id: uuidSchema,
  size_kind: costumeSizeKindSchema,
  label: z.string().trim().min(1).max(80),
  measured_on: isoDateSchema.optional(),
  notes: z.string().trim().max(1000).optional(),
});
export type CostumeSizeInput = z.input<typeof costumeSizeInputSchema>;

export const costumeSizeSchema = z.object({
  id: uuidSchema,
  dancer_id: uuidSchema,
  size_kind: costumeSizeKindSchema,
  label: z.string(),
  measured_on: isoDateSchema,
  notes: z.string().nullable(),
  created_at: isoDateTimeSchema,
});
export type CostumeSize = z.infer<typeof costumeSizeSchema>;

export const recitalPackageInputSchema = z.object({
  production_id: uuidSchema,
  name: z.string().trim().min(1).max(160),
  price_cents: z.number().int().nonnegative(),
  includes_costume: z.boolean().default(true),
  includes_tshirt: z.boolean().default(false),
  included_ticket_count: z.number().int().nonnegative().default(0),
  includes_video: z.boolean().default(false),
  includes_program: z.boolean().default(false),
  due_date: isoDateSchema.optional(),
  active: z.boolean().default(true),
});
export type RecitalPackageInput = z.input<typeof recitalPackageInputSchema>;

export const recitalPackageSchema = timestampedSchema.extend({
  production_id: uuidSchema,
  name: z.string(),
  price_cents: z.number().int().nonnegative(),
  includes_costume: z.boolean(),
  includes_tshirt: z.boolean(),
  included_ticket_count: z.number().int().nonnegative(),
  includes_video: z.boolean(),
  includes_program: z.boolean(),
  due_date: isoDateSchema.nullable(),
  active: z.boolean(),
});
export type RecitalPackage = z.infer<typeof recitalPackageSchema>;

export const dancerRecitalPackageInputSchema = z.object({
  dancer_id: uuidSchema,
  package_id: uuidSchema,
  costume_status: packageItemStatusSchema.default('not_started'),
  tshirt_status: packageItemStatusSchema.default('not_started'),
  ticket_status: packageItemStatusSchema.default('not_started'),
  video_status: packageItemStatusSchema.default('not_started'),
  program_status: packageItemStatusSchema.default('not_started'),
  notes: z.string().trim().max(2000).optional(),
});
export type DancerRecitalPackageInput = z.input<typeof dancerRecitalPackageInputSchema>;

export const dancerRecitalPackageStatusUpdateSchema = z.object({
  id: uuidSchema,
  costume_status: packageItemStatusSchema.optional(),
  tshirt_status: packageItemStatusSchema.optional(),
  ticket_status: packageItemStatusSchema.optional(),
  video_status: packageItemStatusSchema.optional(),
  program_status: packageItemStatusSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type DancerRecitalPackageStatusUpdate = z.input<
  typeof dancerRecitalPackageStatusUpdateSchema
>;

export const dancerRecitalPackageSchema = timestampedSchema.extend({
  dancer_id: uuidSchema,
  package_id: uuidSchema,
  charge_id: uuidSchema.nullable(),
  costume_status: packageItemStatusSchema,
  tshirt_status: packageItemStatusSchema,
  ticket_status: packageItemStatusSchema,
  video_status: packageItemStatusSchema,
  program_status: packageItemStatusSchema,
  notes: z.string().nullable(),
});
export type DancerRecitalPackage = z.infer<typeof dancerRecitalPackageSchema>;
